import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useActionsContext } from './framework/context';
import { Text, Loading } from '@cotera/client/app/components/ui';
import { NotFound } from '@cotera/client/app/components';
import type { Action } from './framework/types';
import { kebabCase } from 'lodash';

const tryParseJson = (raw: string | undefined): unknown => {
  if (!raw || raw.length === 0) {
    return {};
  }
  try {
    const decoded = decodeURIComponent(raw);
    return JSON.parse(decoded);
  } catch {
    return {};
  }
};

export const ActionRouteRunner: React.FC = () => {
  const { actionName, jsonParams } = useParams();
  const navigate = useNavigate();
  const registryContext = useActionsContext();
  const execContext = registryContext;
  const [hasInvoked, setHasInvoked] = useState(false);

  const action: Action | null = useMemo(() => {
    const all = registryContext.getAllActions();
    if (!actionName) {
      return null;
    }
    const lower = actionName.toLowerCase();
    const byConstructor = all.find((a) => a.constructor.name === actionName);
    if (byConstructor) {
      return byConstructor;
    }
    const byTitle = all.find(
      (a) => a.title.toLowerCase() === lower || kebabCase(a.title) === lower
    );
    return byTitle ?? null;
  }, [registryContext, actionName]);

  const payload = useMemo(() => {
    const parsed = tryParseJson(jsonParams) as Record<string, unknown>;
    return parsed as Record<string, unknown>;
  }, [jsonParams]);

  useEffect(() => {
    const invoke = async () => {
      if (hasInvoked || !action) {
        return;
      }
      setHasInvoked(true);
      try {
        await action.askOrExecute(payload, execContext);
      } finally {
        // No redirect by default; leave the running page visible.
        // If actions want to navigate, they can do so via context.
      }
    };
    void invoke();
  }, [action, execContext, hasInvoked, navigate, payload]);

  if (!action) {
    return <NotFound resource="action" />;
  }

  return (
    <div className="flex flex-col gap-3 items-center justify-center h-full w-full">
      <Loading.Dots />
      <div className="flex items-center gap-2">
        {action.icon ? (
          <span
            aria-hidden="true"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-muted text-xs"
            title={action.icon}
          >
            {action.icon.slice(0, 2).toUpperCase()}
          </span>
        ) : null}
        <Text.Title type="section">Running {action.title}</Text.Title>
      </div>
      <Text.Caption className="text-muted-foreground">
        Route action: {action.constructor.name}
      </Text.Caption>
    </div>
  );
};
